// @ts-nocheck
/**
 * Node Registry
 * Imports node definitions and schemas from shared/nodes
 * This is the single source of truth for all node metadata
 */

import {
  ALL_NODE_DEFINITIONS,
  ALL_NODE_SCHEMAS,
  ALL_NODE_PROPERTIES,
  ALL_NODE_DEFAULTS,
  ALL_NODE_VALIDATORS,
} from '@shared/nodes';

import type { NodeDefinition } from '@shared/types/node-definition';
import type { NodePropertyDefinition } from '@shared/types/properties';

// Debug: Log what's in ALL_NODE_PROPERTIES

/**
 * Get node definition by type
 */
export function getNodeDefinition(nodeType: string): NodeDefinition | undefined {
  return ALL_NODE_DEFINITIONS[nodeType];
}

/**
 * Get node schema (property definitions) by type
 */
export function getNodeSchema(nodeType: string): NodePropertyDefinition[] {

  // First check for properties export (UI property schemas)
  const properties = ALL_NODE_PROPERTIES[nodeType];

  if (properties) {
    // Handle both array format and schema object format
    if (Array.isArray(properties)) {
      return properties;
    }
    // If it's a NodePropertySchema object, return the properties array
    const propsArray = (properties as any).properties || [];
    return propsArray;
  }

  // Fallback to schema export (legacy)
  const schema = ALL_NODE_SCHEMAS[nodeType];

  if (!schema) {
    return [];
  }
  if (Array.isArray(schema)) {
    return schema;
  }
  // If it's a NodePropertySchema object, return the properties array
  const schemaProps = (schema as any).properties || [];
  return schemaProps;
}

/**
 * Get full node schema object (including metadata like iteratesOverItems)
 */
export function getFullNodeSchema(nodeType: string): any | undefined {
  const schema = ALL_NODE_PROPERTIES[nodeType] || ALL_NODE_SCHEMAS[nodeType];
  // Return the schema object if it has properties, otherwise undefined
  if (schema && !Array.isArray(schema) && (schema as any).properties) {
    return schema;
  }
  return undefined;
}

/**
 * Check if a node type iterates over items (processes arrays internally)
 */
export function nodeIteratesOverItems(nodeType: string): boolean {
  const schema = getFullNodeSchema(nodeType);
  return schema?.iteratesOverItems === true;
}

/**
 * Get node default values by type
 */
export function getNodeDefaults(nodeType: string): Record<string, any> {
  return ALL_NODE_DEFAULTS[nodeType] || {};
}

/**
 * Get node validator function by type
 */
export function getNodeValidator(nodeType: string) {
  return ALL_NODE_VALIDATORS[nodeType];
}

/**
 * Get all node definitions
 */
export function getAllNodeDefinitions(): Record<string, NodeDefinition> {
  return ALL_NODE_DEFINITIONS;
}

/**
 * Get all node schemas
 */
export function getAllNodeSchemas(): Record<string, NodePropertyDefinition[]> {
  return ALL_NODE_SCHEMAS;
}

/**
 * Get nodes by category
 */
export function getNodesByCategory(category: string): NodeDefinition[] {
  return Object.values(ALL_NODE_DEFINITIONS).filter(
    (def) => def.category === category
  );
}

/**
 * Validate node properties
 */
export function validateNodeProperties(
  nodeType: string,
  properties: Record<string, any>
) {
  const validator = getNodeValidator(nodeType);
  if (!validator) {
    return { valid: true, errors: [] };
  }
  return validator(properties);
}

// Re-export for convenience
export { ALL_NODE_DEFINITIONS, ALL_NODE_SCHEMAS, ALL_NODE_DEFAULTS };
export type { NodeDefinition, NodePropertyDefinition };
